import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { 
  Plus, 
  Search, 
  Filter, 
  Heart, 
  MessageCircle, 
  Eye, 
  Clock,
  Pin,
  TrendingUp,
  Users,
  Lightbulb,
  AlertTriangle,
  MessageSquare,
  X,
  Send,
  MoreHorizontal,
  Edit3,
  Trash2
} from 'lucide-react'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  width: 100%;
  margin: 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }
`

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 30px;
  border-bottom: 1px solid ${props => props.theme.border};
  overflow-x: auto;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  background: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
  color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.textSecondary};
  font-weight: ${props => props.$active ? '600' : '400'};
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.buttonPrimary};
    background-color: ${props => props.theme.hoverBackground};
  }
  
  ${props => props.$active && `
    border-bottom-color: ${props.theme.buttonPrimary};
    background-color: ${props.theme.hoverBackground};
  `}
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`

const TabIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url(${props => {
    switch(props.$type) {
      case 'all': return '/1.svg';
      case 'idea': return '/2.svg';
      case 'bug': return '/3.svg';
      case 'free': return '/4.svg';
      default: return '/1.svg';
    }
  }});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`

const SearchSection = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 14px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const SearchButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${props => props.theme.textMuted};

  &:hover {
    color: ${props => props.theme.buttonPrimary};
  }
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const ContentArea = styled.div`
  display: flex;
  gap: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`

const MainContent = styled.div`
  flex: 1;
`

const Sidebar = styled.div`
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const PostsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PostCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border-left: ${props => props.$isNotice ? '4px solid #ff6b6b' : 'none'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`

const PostHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  position: relative;
`

const PostInfo = styled.div`
  flex: 1;
`

const PostTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 8px;
`

const PostBadge = styled.span`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  background-color: ${props => 
    props.$type === 'notice' ? 'rgba(255, 107, 107, 0.1)' :
    props.$type === 'hot' ? 'rgba(255, 193, 7, 0.1)' :
    props.$type === 'idea' ? 'rgba(76, 175, 80, 0.1)' :
    props.$type === 'bug' ? 'rgba(255, 87, 34, 0.1)' :
    props.$type === 'free' ? 'rgba(33, 150, 243, 0.1)' :
    'rgba(158, 158, 158, 0.1)'
  };
  color: ${props => 
    props.$type === 'notice' ? '#ff6b6b' :
    props.$type === 'hot' ? '#ff9800' :
    props.$type === 'idea' ? '#4caf50' :
    props.$type === 'bug' ? '#ff5722' :
    props.$type === 'free' ? '#2196f3' :
    '#999'
  };
`

const PostMeta = styled.div`
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 8px;
  flex-wrap: wrap;
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const PostContent = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const PostStats = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 12px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const PostActions = styled.div`
  position: relative;
`

const MoreButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${props => props.theme.textSecondary};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.textPrimary};
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 120px;
  overflow: hidden;
  margin-top: 4px;
`

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  &.delete {
    color: #ff5722;
    
    &:hover {
      background-color: rgba(255, 87, 34, 0.1);
    }
  }
`

const SidebarCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`

const PopularPostItem = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme.border};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    margin: 0 -12px;
    padding: 12px;
    border-radius: 6px;
  }
`

const PopularPostTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 4px;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 4px;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const PopularPostMeta = styled.div`
  font-size: 11px;
  color: ${props => props.theme.textSecondary};
  display: flex;
  gap: 8px;
`

const PopularPostBadge = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
  background-color: ${props =>
    props.$type === 'idea' ? 'rgba(76, 175, 80, 0.1)' :
    props.$type === 'bug' ? 'rgba(255, 87, 34, 0.1)' :
    props.$type === 'free' ? 'rgba(33, 150, 243, 0.1)' :
    'rgba(158, 158, 158, 0.1)'
  };
  color: ${props =>
    props.$type === 'idea' ? '#4caf50' :
    props.$type === 'bug' ? '#ff5722' :
    props.$type === 'free' ? '#2196f3' :
    '#999'
  };
`

const PopularTabs = styled.div`
  display: flex;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${props => props.theme.border};
`

const PopularTab = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: none;
  background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.cardBackground};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  font-size: 12px;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.hoverBackground};
  }
`

const PopularLoading = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
`

const WriteButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  z-index: 10;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.6);
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.textSecondary};
`

const EmptyTitle = styled.h3`
  font-size: 20px;
  margin: 0 0 10px 0;
  color: ${props => props.theme.textPrimary};
`

const EmptyDescription = styled.p`
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
  color: ${props => props.theme.textSecondary};
`

// 글쓰기 모달 스타일
const WriteModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`

const ModalContent = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 16px;
  padding: 30px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 1px solid ${props => props.theme.border};
  padding-bottom: 20px;
`

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  color: ${props => props.theme.textSecondary};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.textPrimary};
  }
`

const FormSection = styled.div`
  margin-bottom: 25px;
`

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 12px;
`

const CategorySelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const CategoryOption = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border: 2px solid ${props => props.$selected ? props.theme.buttonPrimary : props.theme.border};
  border-radius: 12px;
  background-color: ${props => props.$selected ? `${props.theme.buttonPrimary}10` : props.theme.cardBackground};
  color: ${props => props.$selected ? props.theme.buttonPrimary : props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  
  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    background-color: ${props => props.theme.buttonPrimary}10;
  }
`

const CategoryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${props => props.$selected ? props.theme.buttonPrimary : props.theme.textSecondary};
  color: white;
  transition: all 0.3s ease;
`

const CategoryInfo = styled.div`
  flex: 1;
`

const CategoryName = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`

const CategoryDesc = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  opacity: 0.8;
`

const FormInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 12px;
  font-size: 16px;
  background-color: ${props => props.theme.backgroundColor};
  color: ${props => props.theme.textPrimary};
  transition: all 0.3s ease;
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 3px ${props => props.theme.buttonPrimary}20;
  }
`

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 12px;
  font-size: 16px;
  background-color: ${props => props.theme.backgroundColor};
  color: ${props => props.theme.textPrimary};
  resize: vertical;
  min-height: 200px;
  font-family: inherit;
  line-height: 1.5;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 3px ${props => props.theme.buttonPrimary}20;
  }
`

const ModalFooter = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme.border};
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const ModalButton = styled.button`
  padding: 14px 28px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: ${props => props.theme.textSecondary};
    border: 2px solid ${props => props.theme.border};
    
    &:hover {
      background-color: ${props => props.theme.hoverBackground};
      border-color: ${props => props.theme.textSecondary};
    }
  }
`

export default function CommunityPage() {
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  
  // 글쓰기 모달 상태
  const [showWriteModal, setShowWriteModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [postTitle, setPostTitle] = useState('')
  const [postContent, setPostContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [popularPosts, setPopularPosts] = useState([])
  const [showDropdown, setShowDropdown] = useState(null)
  // 인기글 탭 상태
  const [popularTab, setPopularTab] = useState('likes') // 'likes' 또는 'views'
  const [loadingPopular, setLoadingPopular] = useState(false)

  // 인기글 데이터 로드
  const loadPopularPosts = async (type = 'likes') => {
    console.log(`=== 인기글 로드 (${type === 'likes' ? '좋아요순' : '조회수순'}) ===`)
    setLoadingPopular(true)
    
    try {
      const response = await fetch(`/community/popular/${type}`)
      console.log('📡 인기글 API 응답:', response.status, response.statusText)
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`)
      }
      
      const result = await response.json()
      console.log('📋 인기글 데이터:', result)
      
      if (result.success) {
        console.log(`✅ ${type === 'likes' ? '좋아요순' : '조회수순'} 인기글 ${result.posts.length}개 로드 성공`)
        setPopularPosts(result.posts)
      } else {
        console.error('❌ 인기글 로드 실패:', result.message)
        setPopularPosts([])
      }
    } catch (error) {
      console.error('❌ 인기글 로드 오류:', error)
      setPopularPosts([])
    } finally {
      setLoadingPopular(false)
    }
  }

  // 게시글 데이터 로드
  const loadPosts = async () => {
    try {
      setLoading(true)
      console.log('📚 게시글 데이터 로드 시작...')
      
      const categoryParam = activeTab === 'all' ? '' : activeTab
      const searchParam = searchTerm || ''
      const url = `/community/posts?category=${categoryParam}&search=${searchParam}`
      
      console.log('🌐 요청 URL:', url)
      const response = await fetch(url)
      console.log('📡 응답 상태:', response.status, response.statusText)
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`)
      }
      
      const result = await response.json()
      console.log('📋 서버 응답:', result)
      
      if (result.success) {
        console.log(`✅ 게시글 ${result.posts.length}개 로드 성공`)
        setPosts(result.posts || [])
      } else {
        console.error('❌ 게시글 로드 실패:', result.message)
        setPosts([])
      }
    } catch (error) {
      console.error('❌ 게시글 로드 오류:', error.message)
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  // 인기글 탭 변경 핸들러
  const handlePopularTabChange = (newTab) => {
    console.log('📊 인기글 탭 변경:', newTab)
    setPopularTab(newTab)
    loadPopularPosts(newTab)
  }

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadPosts()
    loadPopularPosts(popularTab)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // 탭이나 검색어 변경 시 데이터 재로드
  useEffect(() => {
    loadPosts()
  }, [activeTab, searchTerm]) // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: 'all', label: '전체 보기', icon: 'all' },
    { id: 'idea', label: '아이디어 제안', icon: 'idea' },
    { id: 'bug', label: '오류 불편 신고', icon: 'bug' },
    { id: 'free', label: '자유게시판', icon: 'free' }
  ]

  // 카테고리 정의
  const categories = [
    {
      id: 'idea',
      name: '아이디어 제안',
      description: '새로운 기능이나 개선사항을 제안해주세요',
      icon: <Lightbulb size={16} />,
      color: '#4caf50'
    },
    {
      id: 'bug',
      name: '오류 불편 신고',
      description: '버그나 불편한 점을 신고해주세요',
      icon: <AlertTriangle size={16} />,
      color: '#ff5722'
    },
    {
      id: 'free',
      name: '자유게시판',
      description: '자유롭게 의견을 나눠주세요',
      icon: <MessageSquare size={16} />,
      color: '#2196f3'
    }
  ]

  const filteredPosts = posts // 서버에서 이미 필터링된 데이터

  const handleWritePost = () => {
    if (!isLoggedIn) {
      alert('로그인이 필요한 기능입니다.')
      navigate('/login')
      return
    }
    setShowWriteModal(true)
  }

  const handleCloseModal = () => {
    setShowWriteModal(false)
    setSelectedCategory('')
    setPostTitle('')
    setPostContent('')
  }

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId)
  }

  const handleSubmitPost = async () => {
    if (!selectedCategory) {
      alert('카테고리를 선택해주세요.')
      return
    }
    
    if (!postTitle.trim()) {
      alert('제목을 입력해주세요.')
      return
    }
    
    if (!postContent.trim()) {
      alert('내용을 입력해주세요.')
      return
    }

    setIsSubmitting(true)
    
    try {
      console.log('📝 게시글 작성 요청:', {
        category: selectedCategory,
        title: postTitle,
        content: postContent
      })
      
      const response = await fetch('/community/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          title: postTitle.trim(),
          content: postContent.trim(),
          category: selectedCategory,
          user_id: user?.id // 로그인한 사용자 ID
        })
      })
      
      console.log('📡 게시글 작성 응답:', response.status, response.statusText)
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`)
      }
      
      const result = await response.json()
      console.log('📋 게시글 작성 결과:', result)
      
      if (result.success) {
        console.log('✅ 게시글 작성 성공!')
        alert('게시글이 성공적으로 작성되었습니다!')
        handleCloseModal()
        // 게시글 목록 새로고침
        await loadPosts()
      } else {
        throw new Error(result.message || '게시글 작성에 실패했습니다.')
      }
      
    } catch (error) {
      console.error('게시글 작성 실패:', error)
      alert('게시글 작성 중 오류가 발생했습니다.')
      setIsSubmitting(false)
    }
  }

  const handlePostClick = (postId) => {
    console.log('게시글 보기:', postId)
    navigate(`/community/${postId}`)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    
    // 한국 시간으로 변환 (UTC+9)
    const koreaDate = new Date(date.getTime() + (9 * 60 * 60 * 1000))
    const koreaNow = new Date(now.getTime() + (9 * 60 * 60 * 1000))
    
    // 날짜만 비교 (시간 제외)
    const dateOnly = new Date(koreaDate.getFullYear(), koreaDate.getMonth(), koreaDate.getDate())
    const nowOnly = new Date(koreaNow.getFullYear(), koreaNow.getMonth(), koreaNow.getDate())
    
    const diffTime = nowOnly - dateOnly
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) {
      // 오늘 작성된 게시글은 시간 표시
      return koreaDate.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      })
    }
    if (diffDays === 1) return '어제'
    if (diffDays < 7) return `${diffDays}일 전`
    return koreaDate.toLocaleDateString('ko-KR')
  }

  const handleMoreClick = (postId, event) => {
    event.stopPropagation()
    setShowDropdown(showDropdown === postId ? null : postId)
  }

  const handleEditPost = (postId, event) => {
    event.stopPropagation()
    console.log('게시글 수정:', postId)
    // TODO: 게시글 수정 모달 또는 페이지로 이동
    alert('게시글 수정 기능 (구현 예정)')
    setShowDropdown(null)
  }

  const handleDeletePost = async (postId, event) => {
    event.stopPropagation()
    if (!window.confirm('게시글을 삭제하시겠습니까?')) {
      setShowDropdown(null)
      return
    }
    
    try {
      console.log('게시글 삭제:', postId)
      const response = await fetch(`/community/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`)
      }
      
      const result = await response.json()
      if (result.success) {
        alert('게시글이 삭제되었습니다.')
        await loadPosts() // 게시글 목록 새로고침
      } else {
        throw new Error(result.message || '게시글 삭제에 실패했습니다.')
      }
    } catch (error) {
      console.error('게시글 삭제 오류:', error)
      alert('게시글 삭제 중 오류가 발생했습니다.')
    }
    
    setShowDropdown(null)
  }

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown) {
        setShowDropdown(null)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>커뮤니티</Title>
      </Header>

      <TabsContainer>
        {tabs.map(tab => (
          <Tab 
            key={tab.id}
            $active={activeTab === tab.id}
            theme={theme}
            onClick={() => setActiveTab(tab.id)}
          >
            <TabIcon $type={tab.icon} />
            {tab.label}
          </Tab>
        ))}
      </TabsContainer>

      <SearchSection>
        <SearchBar>
          <SearchInput
            theme={theme}
            type="text"
            placeholder="게시글 검색하기..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchButton theme={theme}>
            <Search size={16} />
          </SearchButton>
        </SearchBar>
        <FilterButton theme={theme}>
          <Filter size={16} />
          필터
        </FilterButton>
      </SearchSection>

      <ContentArea>
        <MainContent>
          {loading ? (
            <EmptyState>
              <EmptyTitle theme={theme}>게시글을 불러오는 중...</EmptyTitle>
              <EmptyDescription>
                잠시만 기다려주세요.
              </EmptyDescription>
            </EmptyState>
          ) : filteredPosts.length === 0 ? (
            <EmptyState>
              <EmptyTitle theme={theme}>게시글이 없습니다</EmptyTitle>
              <EmptyDescription>
                첫 번째 게시글을 작성해보세요!
              </EmptyDescription>
            </EmptyState>
          ) : (
            <PostsList>
              {filteredPosts.map(post => (
                <PostCard 
                  key={post.post_id} 
                  theme={theme} 
                  $isNotice={post.is_pinned}
                  onClick={() => handlePostClick(post.post_id)}
                >
                  <PostHeader>
                    <PostInfo>
                      <PostTitle theme={theme}>
                        {post.is_pinned && <Pin size={14} />}
                        {post.title}
                        {post.is_pinned && <PostBadge $type="notice">공지</PostBadge>}
                        {post.category === 'IDEA' && <PostBadge $type="idea">아이디어</PostBadge>}
                        {post.category === 'BUG_REPORT' && <PostBadge $type="bug">버그신고</PostBadge>}
                        {post.category === 'FREE_BOARD' && <PostBadge $type="free">자유</PostBadge>}
                        {post.like_count > 20 && <PostBadge $type="hot">HOT</PostBadge>}
                      </PostTitle>
                      <PostMeta theme={theme}>
                        <MetaItem>
                          <Users size={12} />
                          {post.username || `사용자 ${post.user_id}`}
                        </MetaItem>
                        <MetaItem>
                          <Clock size={12} />
                          {formatDate(post.created_at)}
                        </MetaItem>
                      </PostMeta>
                    </PostInfo>
                    <PostActions>
                      <MoreButton 
                        theme={theme}
                        onClick={(e) => handleMoreClick(post.post_id, e)}
                      >
                        <MoreHorizontal size={16} />
                      </MoreButton>
                      {showDropdown === post.post_id && (
                        <DropdownMenu theme={theme}>
                          <DropdownItem 
                            theme={theme}
                            onClick={(e) => handleEditPost(post.post_id, e)}
                          >
                            <Edit3 size={16} />
                            수정
                          </DropdownItem>
                          <DropdownItem 
                            theme={theme}
                            className="delete"
                            onClick={(e) => handleDeletePost(post.post_id, e)}
                          >
                            <Trash2 size={16} />
                            삭제
                          </DropdownItem>
                        </DropdownMenu>
                      )}
                    </PostActions>
                  </PostHeader>
                  
                  <PostContent theme={theme}>
                    {post.content}
                  </PostContent>
                  
                  <PostStats theme={theme}>
                    <StatItem>
                      <Eye size={12} />
                      {post.view_count || 0}
                    </StatItem>
                    <StatItem>
                      <Heart size={12} />
                      {post.like_count || 0}
                    </StatItem>
                    <StatItem>
                      <MessageCircle size={12} />
                      {post.comment_count || 0}
                    </StatItem>
                  </PostStats>
                </PostCard>
              ))}
            </PostsList>
          )}
        </MainContent>

        <Sidebar>
          <SidebarCard theme={theme}>
            <SectionTitle theme={theme}>
              <TrendingUp size={18} />
              이주의 인기글
            </SectionTitle>
            
            <PopularTabs theme={theme}>
              <PopularTab 
                theme={theme}
                $active={popularTab === 'likes'}
                onClick={() => handlePopularTabChange('likes')}
              >
                ❤️ 좋아요순
              </PopularTab>
              <PopularTab 
                theme={theme}
                $active={popularTab === 'views'}
                onClick={() => handlePopularTabChange('views')}
              >
                👁️ 조회수순
              </PopularTab>
            </PopularTabs>
            
            {loadingPopular ? (
              <PopularLoading theme={theme}>
                인기글을 불러오는 중...
              </PopularLoading>
            ) : popularPosts.length === 0 ? (
              <PopularLoading theme={theme}>
                이주의 인기글이 없습니다
              </PopularLoading>
            ) : (
              popularPosts.map((post, index) => (
                <PopularPostItem
                  key={post.post_id}
                  theme={theme}
                  onClick={() => handlePostClick(post.post_id)}
                >
                  <PopularPostTitle theme={theme}>
                    <span>{index + 1}. {post.title}</span>
                    {post.category === 'IDEA' && <PopularPostBadge $type="idea">아이디어</PopularPostBadge>}
                    {post.category === 'BUG_REPORT' && <PopularPostBadge $type="bug">버그신고</PopularPostBadge>}
                    {post.category === 'FREE_BOARD' && <PopularPostBadge $type="free">자유</PopularPostBadge>}
                  </PopularPostTitle>
                  <PopularPostMeta theme={theme}>
                    {popularTab === 'likes' ? (
                      <>
                        <span>❤️ {post.likes_count || 0}</span>
                        <span>💬 {post.comment_count || 0}</span>
                      </>
                    ) : (
                      <>
                        <span>👁️ {post.views_count || post.view_count || 0}</span>
                        <span>❤️ {post.like_count || 0}</span>
                      </>
                    )}
                  </PopularPostMeta>
                </PopularPostItem>
              ))
            )}
          </SidebarCard>
        </Sidebar>
      </ContentArea>

      <WriteButton onClick={handleWritePost}>
        <Plus size={24} />
      </WriteButton>

      {/* 글쓰기 모달 */}
      {showWriteModal && (
        <WriteModal onClick={handleCloseModal}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ModalTitle theme={theme}>새 글 작성</ModalTitle>
              <CloseButton onClick={handleCloseModal} theme={theme}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <FormSection>
              <FormLabel theme={theme}>카테고리 선택</FormLabel>
              <CategorySelector>
                {categories.map(category => (
                  <CategoryOption
                    key={category.id}
                    theme={theme}
                    $selected={selectedCategory === category.id}
                    onClick={() => handleCategorySelect(category.id)}
                  >
                    <CategoryIcon 
                      $selected={selectedCategory === category.id}
                      theme={theme}
                      style={{ backgroundColor: selectedCategory === category.id ? category.color : theme.textSecondary }}
                    >
                      {category.icon}
                    </CategoryIcon>
                    <CategoryInfo>
                      <CategoryName>{category.name}</CategoryName>
                      <CategoryDesc theme={theme}>{category.description}</CategoryDesc>
                    </CategoryInfo>
                  </CategoryOption>
                ))}
              </CategorySelector>
            </FormSection>

            <FormSection>
              <FormLabel theme={theme}>제목</FormLabel>
              <FormInput
                theme={theme}
                type="text"
                placeholder="제목을 입력해주세요"
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                maxLength={100}
              />
            </FormSection>

            <FormSection>
              <FormLabel theme={theme}>내용</FormLabel>
              <FormTextarea
                theme={theme}
                placeholder="내용을 입력해주세요"
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                maxLength={2000}
              />
              <div style={{ 
                textAlign: 'right', 
                fontSize: '12px', 
                color: theme.textSecondary, 
                marginTop: '8px' 
              }}>
                {postContent.length}/2000
              </div>
            </FormSection>

            <ModalFooter>
              <ModalButton 
                className="secondary" 
                theme={theme} 
                onClick={handleCloseModal}
              >
                취소
              </ModalButton>
              <ModalButton 
                className="primary" 
                onClick={handleSubmitPost}
                disabled={isSubmitting || !selectedCategory || !postTitle.trim() || !postContent.trim()}
              >
                {isSubmitting ? (
                  <>
                    <div style={{ 
                      width: '16px', 
                      height: '16px', 
                      border: '2px solid transparent',
                      borderTop: '2px solid currentColor',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    작성 중...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    작성하기
                  </>
                )}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </WriteModal>
      )}
    </Container>
  )
}